/**
 * Tutor whiteboard persistence: apply an incoming whiteboard message to a
 * store ref keyed by studentId, so the tutor's copy stays in sync when
 * the focused student's handler is unmounted.
 */

import { MESSAGE_TYPES, VALID_SURFACES, STROKE_COLORS } from './constants';

/**
 * Apply a whiteboard app message to the tutor's persisted store.
 * Mutates storeRef.current[studentId].lesson / .whiteboard.
 * @param {{ data: object }} message - App message with data.studentId, data.type, etc.
 * @param {{ current: Record<string, { lesson: array, whiteboard: array }> }} storeRef
 * @param {boolean} isTutor - True when applying on tutor (affects default stroke color)
 */
export function applyWhiteboardMessageToStore(message, storeRef, isTutor = true) {
  if (!storeRef?.current || typeof storeRef.current !== 'object') return;

  const data = message?.data && typeof message.data === 'object' ? message.data : message;
  if (!data?.type) return;

  const studentId = data.studentId;
  if (!studentId || typeof studentId !== 'string') return;

  if (!storeRef.current[studentId]) {
    storeRef.current[studentId] = { lesson: [], whiteboard: [] };
  }
  const entry = storeRef.current[studentId];

  if (data.type === MESSAGE_TYPES.STROKE) {
    const { strokeId, points, senderId, surface } = data;
    if (!strokeId || !Array.isArray(points) || points.length < 1 || !VALID_SURFACES.includes(surface)) return;

    const color = (typeof data.color === 'string' && data.color)
      ? data.color
      : (isTutor ? STROKE_COLORS.student : STROKE_COLORS.tutor);
    const validPoints = points.filter((p) => Array.isArray(p) && p.length >= 2);
    if (validPoints.length === 0) return;

    const arr = surface === 'lesson' ? entry.lesson : entry.whiteboard;
    const existing = arr.find((s) => s.strokeId === strokeId);
    if (existing) {
      existing.points.push(...validPoints);
    } else {
      arr.push({ strokeId, points: [...validPoints], color, senderId });
    }
  } else if (data.type === MESSAGE_TYPES.ERASE) {
    const { strokeIds, surface } = data;
    if (!Array.isArray(strokeIds) || strokeIds.length < 1 || !VALID_SURFACES.includes(surface)) return;

    const arr = surface === 'lesson' ? entry.lesson : entry.whiteboard;
    const ids = new Set(strokeIds);
    entry[surface === 'lesson' ? 'lesson' : 'whiteboard'] = arr.filter((s) => !ids.has(s.strokeId));
  } else if (data.type === MESSAGE_TYPES.CLEAR) {
    if (data.surface && VALID_SURFACES.includes(data.surface)) {
      entry[data.surface === 'lesson' ? 'lesson' : 'whiteboard'] = [];
    } else {
      entry.lesson = [];
      entry.whiteboard = [];
    }
  }
}
